import React, { useContext, useState } from "react";
import { Button } from "theme-ui";
import { Form, Formik } from "formik";
import { createDepot } from "api/Providers/Depots";
import * as Yup from "yup";

import { filterByKeys } from "helpers/default";


import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { useToast } from "components/toast";
import BasicButton from "components/forms/BasicButton";
import { CheckMarkIcon, PlusIcon, ProvidersIcon } from "components/svg";
import TextInput from "components/forms/TextInput";
import GooglePlaces from "components/forms/GooglePlaces";
import Checkbox from "components/forms/Checkbox";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

export default function CreateDepot({ provider, includeButton, afterCreate, isOpen = false }: { provider: any; includeButton?: boolean; isOpen?: boolean; afterCreate?: (val?: any) => void }) {

  const toast = useToast();  
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const { user } = useContext<any>(UserContext);


  const _validate = Yup.object().shape({
    contactEmail: Yup.string().email("Invalid email").required("Email is required."),
    name: Yup.string().required("Depot name is required."),    
  });

  async function _onSubmit(values: any) {    
    if (!provider) return;      

    const keys = ["name","contactEmail","address.line1","address.line2","address.city","address.state","address.postalCode","address.country","latitude","longitude","preferred"];    
    const create = filterByKeys(keys,values);

    const res = await createDepot({ provider, create });
    if(res?.id){ 
      toast.add("Depot created","var(--theme-ui-colors-success)");      
      void trackActivity({
        module: "providers",
        action: "create_depot",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "depot",
        entityId: res.id,
        metadata: {
          providerId: provider?.id ?? null,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setIsModalOpen(false);
      if(typeof afterCreate === 'function') afterCreate(res);
    } else{
      toast.add(res?.message ? res.message : "Error creating depot","var(--theme-ui-colors-danger)");
    }  
  }

  if (!provider) return null;

  return (
    <>
      {includeButton && <BasicButton type="button" $iconHeight="10px" scheme="secondaryOutline" onClick={() => setIsModalOpen(!isModalOpen)}><PlusIcon fill="#000"/>Add depot</BasicButton>}
      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} centered size={"sm"}>
        <Formik initialValues={{}} onSubmit={_onSubmit} validationSchema={_validate} enableReinitialize>
          {({ isSubmitting, values, setFieldValue, submitCount, errors }: { isSubmitting: boolean, values: any, setFieldValue: (name: string, val?: any) => void, submitCount: number, errors: any }) => { 
            return (                
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
                  <ModalIcon><ProvidersIcon fill={"000"}/></ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Add Depot</ModalTitle>
                    <ModalSubtitle>{"Add a depot to a provider. Once saved you will be able to configure the depot further."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>              
                <ModalBody> 
                  <TextInput
                    name="name"
                    label="Depot Name"
                    value={values.name}
                    onChange={(val: any) => setFieldValue("name",val)}
                    $errors={errors?.name && submitCount > 0 ? errors?.name : null}    
                  /> 
                  <TextInput
                    type="email"
                    name="contactEmail"
                    label="Contact Email"
                    value={values.contactEmail}
                    onChange={(val: any) => setFieldValue("contactEmail",val)}
                    $errors={errors?.contactEmail && submitCount > 0 ? errors?.contactEmail : null}    
                  />   
                  <GooglePlaces
                    label="Address"            
                    onChange={(val: any) => {
                      setFieldValue("address",val?.address)
                      setFieldValue("latitude",val?.latitude)
                      setFieldValue("longitude",val?.longitude)
                    }}
                    $errors={(errors?.address || errors?.latitude || errors?.longitude) && submitCount > 0 ? (errors?.address ?? errors?.latitude ?? errors?.longitude) : null}    
                  />
                  <Checkbox
                    label="Preferred"
                    checked={values.preferred}                     
                    onChange={(val: any) => setFieldValue("preferred",!values.preferred)}
                    $customStyles={{marginBottom: "30px"}}
                  />
                </ModalBody>
                <ModalFooter>
                  <Button
                    variant="secondaryOutline"
                    type="button" 
                    onClick={() => setIsModalOpen(false)} 
                  >Cancel</Button>   
                  <BasicButton
                    type="submit" 
                    $submitting={isSubmitting}
                    $iconHeight="14px"
                    $iconWidth="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  >
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"}/>}
                    Save Depot
                  </BasicButton>
                </ModalFooter>            
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
}
